// client/src/utils/safe-url.ts
//
// Redact known-sensitive query parameters from a URL before logging or
// forwarding it to a third-party sink. Used by:
//   - client/src/analytics/posthog.js  (before_send filter for $current_url / $referrer)
//   - client/src/components/ErrorBoundary.tsx  (url sent to /api/client-error)
//
// The deny list MUST stay in sync with server/utils/safe-url.js.
// Both copies carry the same keys; either one needs to be updated when a
// new sensitive URL parameter is added.

export const SENSITIVE_URL_KEYS = [
  'success',
  'token',
  'refresh_token',
  'access_token',
  'jwt_token',
  'user',
  'code',
  'state',
  'error',
  'error_description',
];

function redactSearchParams(url: URL): boolean {
  let changed = false;
  for (const key of SENSITIVE_URL_KEYS) {
    if (url.searchParams.has(key)) {
      url.searchParams.set(key, 'REDACTED');
      changed = true;
    }
  }
  return changed;
}

function redactHashParams(url: URL): boolean {
  if (url.hash.length <= 1) return false;
  const hashParams = new URLSearchParams(url.hash.slice(1));
  let changed = false;
  for (const key of SENSITIVE_URL_KEYS) {
    if (hashParams.has(key)) {
      hashParams.set(key, 'REDACTED');
      changed = true;
    }
  }
  if (changed) url.hash = `#${hashParams.toString()}`;
  return changed;
}

// Returns the input unchanged if it isn't a parseable URL, or if no
// sensitive key was present. Avoids producing a different string when
// the log message wasn't going to leak anything, so existing log search
// by URL keeps working.
export function safeUrl(value: unknown): unknown {
  if (typeof value !== 'string' || value === '') return value;
  try {
    const url = new URL(value);
    const searchChanged = redactSearchParams(url);
    const hashChanged = redactHashParams(url);
    return searchChanged || hashChanged ? url.toString() : value;
  } catch {
    return value;
  }
}